import InfoRounded from '@mui/icons-material/InfoRounded';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import { css, styled, type Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography, { type TypographyProps } from '@mui/material/Typography';
import i18n from 'i18next';
import { Fragment, useCallback } from 'react';

import { Checkbox } from '../Checkbox/Checkbox.tsx';
import { CopyToClipboard } from '../CopyToClipboard/CopyToClipboard.tsx';
import { DateLabel } from '../DateLabel/DateLabel.tsx';
import { Loader } from '../Loader/Loader.tsx';
import { SWITCH_FIELD_DESIGN_SYSTEM_KEYS } from '../SwitchField/switchFieldDesignSystem.ts';
import { DesignSystemScope } from '../theme/DesignSystemScope.tsx';
import { useDesignSystemEnabled } from '../theme/useDesignSystemEnabled.ts';

export interface FieldDetailsProps {
  title: string;
  value?: React.ReactNode;
  customEmptyValue?: string;
  checkbox?: boolean;
  loading?: boolean;
  copy?: boolean;
  toggle?: boolean;
  dateFormat?: string;
  showOffset?: boolean;
  onOffControl?: boolean;
  textarea?: boolean;
  fullWidth?: boolean;
  customStyles?: {
    box?: { spacingBottom?: number; spacingTop?: number; paddingLeft?: string; borderLeft?: string };
    onOffControlTitle?: { spacingTop?: number; spacingBottom?: number };
  };
  tooltipIconText?: string;
  hideTitle?: boolean;
  dataTest?: string;
  copyTooltipText?: string;
  titleTooltipText?: string;
  rows?: number;
}

const sharedBoxStyles = (theme: Theme, customStyles: FieldDetailsProps['customStyles']) => css`
  margin-bottom: ${theme.spacing(customStyles?.box?.spacingBottom ?? 2)};
  margin-top: ${theme.spacing(customStyles?.box?.spacingTop ?? 0)};

  border-left: ${customStyles?.box?.borderLeft ?? 'none'};
  padding-left: ${customStyles?.box?.paddingLeft ?? 0};

  &:last-child {
    margin-bottom: 0;
  }
`;

const StyledBox = styled(Box, { shouldForwardProp: prop => prop !== 'customStyles' })<{
  customStyles: FieldDetailsProps['customStyles'];
}>`
  ${({ theme, customStyles }) => sharedBoxStyles(theme, customStyles)}
`;

const StyledFormControl = styled(FormControl, { shouldForwardProp: prop => prop !== 'customStyles' })<{
  customStyles: FieldDetailsProps['customStyles'];
}>`
  ${({ theme, customStyles }) => sharedBoxStyles(theme, customStyles)}
`;

const StyledTitleTypography = (props: TypographyProps) => (
  <Typography {...props} sx={{ color: 'text.secondary', lineHeight: '16px', mb: 0.75 }} />
);

const StyledEmptyValueTypography = styled(Typography, {
  shouldForwardProp: prop => prop !== 'isEmpty',
})<TypographyProps & { isEmpty: boolean }>`
  ${({ isEmpty, theme }) =>
    isEmpty &&
    css`
      color: ${theme.palette.text.disabled};
    `}
`;

const StyledSpan = styled('span')`
  display: block;
  word-break: break-word;
`;

const StyledCopyToClipboard = styled(CopyToClipboard)`
  vertical-align: middle;
`;

const StyledTextField = styled(TextField)`
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const StyledOnOffTitleTypography = styled(Typography, { shouldForwardProp: prop => prop !== 'customStyles' })<{
  customStyles: FieldDetailsProps['customStyles'];
}>`
  margin-bottom: ${({ theme, customStyles }) => theme.spacing(customStyles?.onOffControlTitle?.spacingBottom ?? 1)};
  margin-top: ${({ theme, customStyles }) => theme.spacing(customStyles?.onOffControlTitle?.spacingTop ?? 3)};
`;

const StyledOnOffControlSpan = styled('span')<{ isOff: boolean }>`
  color: ${({ theme }) => theme.palette.success.main};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  margin-right: ${({ theme }) => theme.spacing(0.5)};
  white-space: nowrap;

  ${({ theme, isOff }) =>
    isOff &&
    css`
      color: ${theme.palette.error.main};
    `};
`;

export function FieldDetails({
  title,
  value,
  checkbox,
  loading,
  copy,
  toggle,
  dateFormat,
  showOffset,
  onOffControl,
  customStyles,
  customEmptyValue,
  textarea,
  rows = 4,
  tooltipIconText,
  fullWidth = true,
  hideTitle = false,
  dataTest,
  copyTooltipText,
  titleTooltipText,
}: FieldDetailsProps) {
  const isEmpty = !(Array.isArray(value) ? value.length : value);
  const isDesignSystem = useDesignSystemEnabled('switchField');

  const renderValue = useCallback(
    (loading, copy, value, title, dateFormat) => {
      const renderList = Array.isArray(value) ? value : [value];
      if (loading) return <Loader size={20} noPadding />;
      if (!isEmpty && (copy || tooltipIconText)) {
        return (
          <Stack
            data-test={dataTest ?? `${title.toLowerCase().replace(/\s/g, '-')}-value`}
            direction="row"
            sx={{
              alignItems: 'center',
            }}
          >
            {renderList.map((element, index) => (
              <Fragment key={index}>
                <Typography sx={{ wordBreak: 'break-word' }}>{element}</Typography>

                {copy && <StyledCopyToClipboard iconSize="medium" text={value} tooltipText={copyTooltipText} />}

                {tooltipIconText && (
                  <Tooltip placement="right" title={tooltipIconText}>
                    <IconButton data-test="tooltip-icon">
                      <InfoRounded />
                    </IconButton>
                  </Tooltip>
                )}
              </Fragment>
            ))}
          </Stack>
        );
      }

      return (
        <StyledEmptyValueTypography
          component="div"
          data-test={dataTest ?? `${title.toLowerCase().replace(/\s/g, '-')}-value`}
          variant="body1"
          isEmpty={isEmpty}
        >
          {!isEmpty
            ? renderList.map((el, idx) => (
                <StyledSpan key={idx}>
                  {dateFormat ? (
                    <DateLabel
                      date={el}
                      displayLocalTime={false}
                      displayExactDate
                      showOffset={showOffset ?? true}
                      dateFormat={dateFormat}
                    />
                  ) : (
                    el
                  )}
                </StyledSpan>
              ))
            : (customEmptyValue ?? i18n.t('N / A'))}
        </StyledEmptyValueTypography>
      );
    },
    [isEmpty, tooltipIconText, dataTest, customEmptyValue, copyTooltipText, showOffset]
  );

  if (checkbox) {
    return (
      <FormControl data-test={dataTest ?? `${title.toLowerCase().replace(/\s/g, '-')}`} fullWidth={fullWidth}>
        <FormControlLabel control={<Checkbox checked={!isEmpty} disabled />} label={title} />
      </FormControl>
    );
  }

  if (textarea) {
    return (
      <StyledBox customStyles={customStyles} data-test={title.toLowerCase().replace(/\s/g, '-')}>
        <StyledTitleTypography
          data-test={`${title.toLowerCase().replace(/\s/g, '-')}-title`}
          variant="body2"
          color="textSecondary"
        >
          {title}
        </StyledTitleTypography>
        {isEmpty ? (
          <StyledEmptyValueTypography
            component="div"
            data-test={`${title.toLowerCase().replace(/\s/g, '-')}-value`}
            variant="body1"
            isEmpty={isEmpty}
          >
            {i18n.t('N / A')}
          </StyledEmptyValueTypography>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <StyledTextField
              multiline
              rows={rows}
              fullWidth={fullWidth}
              value={value}
              slotProps={{ input: { readOnly: true } }}
            />
            {copy && <CopyToClipboard tooltipText={copyTooltipText} text={value as string} iconSize="medium" />}
          </Box>
        )}
      </StyledBox>
    );
  }

  if (toggle) {
    const on = value === '1' || value === true;
    const valueText = on ? 'On' : 'Off';
    const toggleDataTest = dataTest ?? `${title.toLowerCase().replace(/\s/g, '-')}`;

    if (isDesignSystem) {
      return (
        <DesignSystemScope keys={SWITCH_FIELD_DESIGN_SYSTEM_KEYS}>
          <StyledFormControl customStyles={customStyles} data-test={toggleDataTest} fullWidth={fullWidth}>
            <FormControlLabel
              disabled
              sx={{ marginLeft: '-4px' }}
              control={<Switch checked={on} slotProps={{ input: { 'aria-label': title } }} />}
              label={title}
            />
          </StyledFormControl>
        </DesignSystemScope>
      );
    }

    return (
      <StyledFormControl customStyles={customStyles} data-test={toggleDataTest} fullWidth={fullWidth}>
        <FormControlLabel
          control={
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
              <Switch checked={on} disabled slotProps={{ input: { 'aria-label': title } }} />
              <StyledOnOffControlSpan data-test="switch-value" isOff={!on}>
                {valueText}
              </StyledOnOffControlSpan>
            </Box>
          }
          label={title}
        />
      </StyledFormControl>
    );
  }

  if (onOffControl) {
    const on = value === '1' || value === true;
    const valueText = on ? 'On' : 'Off';

    return (
      <StyledBox customStyles={customStyles} data-test={title.toLowerCase().replace(/\s/g, '-')}>
        <StyledOnOffTitleTypography
          customStyles={customStyles}
          data-test={dataTest ?? `${title.toLowerCase().replace(/\s/g, '-')}-title`}
          variant="body1"
        >
          <StyledOnOffControlSpan isOff={!on}>{valueText}</StyledOnOffControlSpan>
          {title}
        </StyledOnOffTitleTypography>
      </StyledBox>
    );
  }

  return (
    <StyledBox
      customStyles={customStyles}
      data-test={title.includes('(') ? 'item-title' : `${title.toLowerCase().replace(/\s/g, '-')}`}
    >
      {!hideTitle && (
        <StyledTitleTypography
          data-test={`${title.toLowerCase().replace(/\s/g, '-')}-title`}
          variant="body2"
          color="textSecondary"
        >
          {title}
          {titleTooltipText && (
            <Tooltip title={titleTooltipText} placement="right">
              <IconButton sx={{ p: 0, marginTop: '-2px', ml: 0.5 }} size="small">
                <InfoRounded />
              </IconButton>
            </Tooltip>
          )}
        </StyledTitleTypography>
      )}
      {renderValue(loading, copy, value, title, dateFormat)}
    </StyledBox>
  );
}
